'use client';

import React, { useMemo } from 'react';
import { tv } from 'tailwind-variants';
import type { FormInstance, StepConfig } from '../../types';
import { getFormInstanceSummary } from '../../utils/formSummary';
import useDictionary from 'lib/hooks/useDictionary';

import type { FieldConfig } from '../../types';

interface InlineCardProps {
  instance: FormInstance;
  instanceIndex: number;
  steps: StepConfig[];
  title: string;
  onEdit: (instanceId: string) => void;
  displayTitleFields?: string[];
  displayFields?: string[];
  showEdit?: boolean;
  editFields?: FieldConfig[]; // New fields that may not be in steps
  isRadioInlineCard?: boolean;
}

export const InlineCard: React.FC<InlineCardProps> = ({
  instance,
  steps,
  onEdit,
  displayTitleFields = ['firstName', 'middleName', 'lastName'],
  displayFields,
  showEdit = true,
  editFields,
  isRadioInlineCard = false,
}) => {
  const { getDictionaryValue } = useDictionary();
  const summary = useMemo(() => {
    // Get summary from steps
    const baseSummary = getFormInstanceSummary(steps, instance.data);

    // Add editFields data if they exist and have values
    if (editFields && editFields.length > 0) {
      editFields.forEach((editField) => {
        const value = instance.data[editField.id];
        if (value !== undefined && value !== null && value !== '') {
          // Check if this field is already in the summary
          const exists = baseSummary.some((item) => item.fieldId === editField.id);
          if (!exists) {
            let displayValue = String(value);
            // Handle select/radio options - show label if available
            if ((editField.type === 'select' || editField.type === 'radio') && editField.options) {
              const option = editField.options.find((opt) => opt.value === String(value));
              if (option) {
                displayValue = option.label;
              }
            }
            baseSummary.push({
              label: editField.label,
              value: displayValue,
              fieldId: editField.id,
            });
          }
        } else if (editField.id === 'childAmount' || editField.id === 'spousePartnerChildAmount') {
          baseSummary.push({
            label: editField.label,
            value: '0',
            fieldId: editField.id,
          });
        }
      });
    }

    return baseSummary;
  }, [steps, instance.data, editFields]);

  const displayTitle = useMemo(() => {
    // If displayTitleFields are configured, use them
    if (displayTitleFields && displayTitleFields.length > 0) {
      const titleParts = displayTitleFields
        .map((fieldId) => {
          // Find the field value from instance data
          const value = instance.data[fieldId];
          return value ? String(value).trim() : null;
        })
        .filter(Boolean);

      return titleParts.length > 0 ? titleParts.join(' ') : null;
    }

    // Find name fields in summary using fieldId
    const firstName = summary.find(
      (item) =>
        item.fieldId === 'firstName' ||
        item.fieldId === 'childFirstName' ||
        item.fieldId === 'spousePartnerFirstName'
    )?.value;
    const middleName = summary.find(
      (item) =>
        item.fieldId === 'middleName' ||
        item.fieldId === 'childMiddleName' ||
        item.fieldId === 'spousePartnerMiddleName'
    )?.value;
    const lastName = summary.find(
      (item) =>
        item.fieldId === 'lastName' ||
        item.fieldId === 'childLastName' ||
        item.fieldId === 'spousePartnerLastName'
    )?.value;

    // Combine name parts, filtering out empty values
    const nameParts = [firstName, middleName, lastName]
      .filter(Boolean)
      .map((part) => String(part).trim());

    return nameParts.length > 0 ? nameParts.join(' ') : null;
  }, [summary, displayTitleFields, instance.data]);

  // Filter summary based on displayFields if provided
  const filteredSummary = useMemo(() => {
    let filtered = summary;

    // If displayFields is specified, only show those fields
    if (displayFields && displayFields.length > 0) {
      // Match by fieldId directly
      filtered = summary.filter((item) => displayFields.includes(item.fieldId));
      const isSpousePartnerCard = displayFields.some(
        (fieldId) =>
          fieldId === 'spousePartnerFirstName' ||
          fieldId === 'spousePartnerMiddleName' ||
          fieldId === 'spousePartnerLastName'
      );
      if (isSpousePartnerCard) {
        // Add static relationship field

        filtered.push({
          label: 'Relationship',
          value: 'spouse/partner',
          fieldId: 'spousePartnerRelationship',
        });
        filtered = filtered.filter((item) => item.fieldId === 'spousePartnerRelationship');
      }
    } else {
      // Original logic: filter out displayTitleFields using fieldId
      if (displayTitleFields && displayTitleFields.length > 0) {
        filtered = summary.filter((item) => !displayTitleFields.includes(item.fieldId));
      } else {
        // Filter out common name fields by fieldId
        filtered = summary.filter((item) => {
          const fieldId = item.fieldId.toLowerCase();
          return (
            fieldId !== 'firstname' &&
            fieldId !== 'middlename' &&
            fieldId !== 'lastname' &&
            fieldId !== 'childFirstName' &&
            fieldId !== 'childMiddleName' &&
            fieldId !== 'childLastName' &&
            fieldId !== 'spousePartnerFirstName' &&
            fieldId !== 'spousePartnerMiddleName' &&
            fieldId !== 'spousePartnerLastName'
          );
        });
      }
    }

    return filtered;
  }, [summary, displayTitleFields, displayFields]);

  const {
    cardContainer,
    cardHeader,
    cardTitle,
    editButton,
    summaryContainer,
    summaryRow,
    summaryLabel,
    summaryValue,
    inlineEditButton,
  } = TAILWIND_VARIANTS();

  return (
    <div className={cardContainer()}>
      <div className={cardHeader()}>
        <span className={cardTitle()}>{displayTitle}</span>
        {showEdit && onEdit && !isRadioInlineCard && (
          <button
            type="button"
            onClick={() => onEdit(instance.instanceId)}
            className={editButton()}
            aria-label={getDictionaryValue('edit') || 'Edit'}
          >
            {getDictionaryValue('edit') || 'Edit'}
          </button>
        )}
      </div>
      <div className={summaryContainer()}>
        {filteredSummary.map((item, idx) => {
          return (
            <div key={idx} className={summaryRow()}>
              <span className={summaryLabel()}>{item.label}:</span>
              <span className={summaryValue()}>
                {item.fieldId === 'cashGiftAmount' ||
                item.fieldId === 'cashGiftToSomeoneAmount' ||
                item.fieldId === 'estimatedPropertyValue' ||
                item.fieldId === 'estimatedCashEstateValue'
                  ? '£'
                  : ''}
                {item.value}
                {item.fieldId === 'charityPercentage' ||
                item.fieldId === 'childAmount' ||
                item.fieldId === 'businessOwnershipPercentage' ||
                item.fieldId === 'spousePartnerChildAmount'
                  ? '%'
                  : ''}
                {item.fieldId === 'childAmount' || item.fieldId === 'spousePartnerChildAmount'
                  ? showEdit &&
                    onEdit && (
                      <button
                        type="button"
                        onClick={() => onEdit(instance.instanceId)}
                        className={inlineEditButton()}
                        aria-label={getDictionaryValue('edit') || 'Edit'}
                      >
                        {getDictionaryValue('edit') || 'Edit'}
                      </button>
                    )
                  : ''}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TAILWIND_VARIANTS = tv({
  slots: {
    cardContainer: [
      'w-full',
      'max-w-[410px]',
      'flex',
      'flex-col',
      'gap-spacing-spacing-32',
      'p-component-card-item-portrait-content-padding-x',
      'bg-Surface-surface-default',
      'shadow-[0_0_8px_0_var(--color-black-alpha-10)]',
      'rounded-component-card-item-image-radius',
    ],
    cardHeader: ['flex', 'flex-row', 'justify-between', 'gap-spacing-spacing-5'],
    cardTitle: [
      'font-typography-font-family-body',
      'text-typography-font-size-small',
      'leading-typography-line-height-small',
      'font-semibold',
      'text-component-section-body',
    ],
    editButton: [
      'text-color-core-color-4-skyblue-darkTint',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      'font-bold',
      'underline',
      'hover:no-underline',
    ],
    inlineEditButton: [
      'ml-4',
      'text-color-core-color-4-skyblue-darkTint',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      'font-bold',
      'underline',
      'hover:no-underline',
    ],
    summaryContainer: ['flex', 'flex-col', 'gap-spacing-spacing-16'],
    summaryRow: ['flex', 'flex-row', 'gap-spacing-spacing-16'],
    summaryLabel: [
      'w-1/2',
      'font-typography-font-family-body',
      'text-ypography-font-size-body-medium',
      'leading-[150%]',
      'font-bold',
      'text-color-core-color-1-Navy',
    ],
    summaryValue: [
      'w-1/2',
      'font-typography-font-family-body',
      'text-ypography-font-size-body-medium',
      'leading-[150%]',
      'font-normal',
      'text-component-section-body',
    ],
  },
});
